import * as yup from 'yup';
import _get from 'lodash/get';
import { BANNER_CTA_TYPE } from 'commons/models/banner.model';
import {
	Row,
	RenderConfig,
	Column,
	TextInput,
	SelectInput,
	AssetUploadInputOnS3,
	EMPTY_ARRAY,
	ASSET_TYPE,
	NumberInput
} from 'commons-ui';

import { APP_LANGUAGE } from 'commons/models/common';
import { ZUPEE_GAMES } from 'pages/dashboard/tournamentV2/utils/constants';
import _isEmpty from 'lodash/isEmpty';
import { BASE_FORM_FIELD_CONFIG, BASE_FORM_FIELD_KEYS, baseValidationSchema, setValueUndefined } from '../../../utils/formConfig';
import { ELaunchWidgetType, ENoticeTypes, LAUNCH_WIDGET_TYPE_OPTIONS } from '../../../utils/constants';

export const ctaParamsGameNameDependencyResolver = (
	props: any,
	dependencyValues: any
): {
	disabled: boolean;
	InputComponent?: typeof SelectInput;
} => {
	const ctaType = _get(dependencyValues, '0');
	const { disabled } = props;

	if ((ctaType === undefined || ctaType === null) && !disabled) {
		setValueUndefined(props);
		return { disabled: true };
	}
	if (ctaType === BANNER_CTA_TYPE.IN_APP) {
		return { InputComponent: SelectInput, disabled: false };
	}

	return { disabled: true };
};

export const displayTextDependencyResolver = (
	props: any,
	dependencyValues: any
): {
	disabled: boolean;
	InputComponent?: typeof TextInput;
} => {
	const ctaType = _get(dependencyValues, '0');
	const { disabled } = props;

	if ((ctaType === undefined || ctaType === null) && !disabled) {
		setValueUndefined(props);
		return { disabled: true };
	}
	if (!_isEmpty(ctaType)) {
		return { InputComponent: TextInput, disabled: false };
	}

	return { disabled: true };
};

export const LAUNCH_WIDGET_FORM_FIELD_KEYS = {
	...BASE_FORM_FIELD_KEYS,
	LAUNCH_WIDGET_TYPE: 'launchWidgetType',
	VIDEO_META: 'videoMeta',
	VIDEO_URL: 'videoUrl',
	VIDEO_DURATION: 'videoDuration',
	CTA_PARAMS: 'ctaParams',
	CTA_PARAMS_GAMENAME: 'ctaParamsGameName',
	CTA_DISPLAY_TEXT: 'ctaDisplayText',
	CTA_DISPLAY_EN_TEXT: 'ctaDisplayENText',
	CTA_DISPLAY_HI_TEXT: 'ctaDisplayHIText'
};

export const launchWidgetValidationSchema = baseValidationSchema.shape({
	launchWidgetType: yup.string().oneOf(Object.values(ELaunchWidgetType)).required('Launch widget type is required'),
	videoMeta: yup.object().shape({
		url: yup.string().url().required('Please upload video'),
		duration: yup.number().required('Add video duration')
	}),
	cta: yup
		.object()
		.shape({
			type: yup.string(),
			value: yup.mixed().when('type', {
				is: (type: string) => type !== undefined && type != null,
				then: yup.string().required('value is required when type is present'),
				otherwise: yup.mixed().test('is-undefined', 'value must be undefined if type is empty', (value) => {
					return value === undefined;
				})
			}),
			params: yup
				.object()
				.shape({
					gameName: yup.string().uppercase().optional()
				})
				.optional(),
			displayText: yup.mixed().when('type', {
				is: (type: string) => type !== undefined && type != null,
				then: yup.object().shape({
					[APP_LANGUAGE.ENGLISH]: yup.string().required('English display text required'),
					[APP_LANGUAGE.HINDI]: yup.string().optional()
				}),
				otherwise: yup.object().shape({
					[APP_LANGUAGE.ENGLISH]: yup.mixed().notRequired(),
					[APP_LANGUAGE.HINDI]: yup.mixed().notRequired()
				})
			})
		})
		.optional()
});

export const LAUNCH_WIDGET_FORM_FIELD_CONFIG = {
	...BASE_FORM_FIELD_CONFIG,
	[LAUNCH_WIDGET_FORM_FIELD_KEYS.LAUNCH_WIDGET_TYPE]: new RenderConfig()
		.setName('launchWidgetType')
		.setLabel('Launch Widget Type')
		.setRenderer(SelectInput)
		.setRenderProps({
			isEditable: { create: false, edit: false },
			options: LAUNCH_WIDGET_TYPE_OPTIONS,
			required: true
		}),
	[LAUNCH_WIDGET_FORM_FIELD_KEYS.VIDEO_META]: new RenderConfig()
		.setName('videoMeta')
		.setLabel('Video Meta Data')
		.setRenderProps({ required: true }),
	[LAUNCH_WIDGET_FORM_FIELD_KEYS.VIDEO_URL]: new RenderConfig()
		.setName('url')
		.setLabel('Upload Video')
		.setRenderer(AssetUploadInputOnS3)
		.setRenderProps({
			isEditable: { create: true, edit: true },
			required: true,
			assetType: ASSET_TYPE.LAUNCH_VIDEO,
			usePresignedUrl: true,
			maxFileSizeMB: 5
		}),
	[LAUNCH_WIDGET_FORM_FIELD_KEYS.VIDEO_DURATION]: new RenderConfig()
		.setName('duration')
		.setLabel('Video Duration In (s)')
		.setRenderer(NumberInput)
		.setRenderProps({
			isEditable: { create: true, edit: true },
			required: true
		}),
	[LAUNCH_WIDGET_FORM_FIELD_KEYS.CTA_PARAMS]: new RenderConfig()
		.setName('params')
		.setLabel('Params')
		.setRenderProps({ required: false }),
	[LAUNCH_WIDGET_FORM_FIELD_KEYS.CTA_PARAMS_GAMENAME]: new RenderConfig()
		.setName('gameName')
		.setLabel('Game Name')
		.setRenderer(SelectInput)
		.setRenderProps({
			isEditable: { create: true, edit: true },
			options: ZUPEE_GAMES,
			dependencies: ['cta.type'],
			dependencyResolver: ctaParamsGameNameDependencyResolver,
			required: false
		}),
	[LAUNCH_WIDGET_FORM_FIELD_KEYS.CTA_DISPLAY_TEXT]: new RenderConfig()
		.setName('displayText')
		.setLabel('Display Text')
		.setRenderProps({ required: false }),
	[LAUNCH_WIDGET_FORM_FIELD_KEYS.CTA_DISPLAY_EN_TEXT]: new RenderConfig()
		.setName(APP_LANGUAGE.ENGLISH)
		.setLabel('English')
		.setRenderer(TextInput)
		.setRenderProps({
			isEditable: { create: true, edit: true },
			required: true,
			dependencies: ['cta.type'],
			dependencyResolver: displayTextDependencyResolver
		}),
	[LAUNCH_WIDGET_FORM_FIELD_KEYS.CTA_DISPLAY_HI_TEXT]: new RenderConfig()
		.setName(APP_LANGUAGE.HINDI)
		.setLabel('Hindi')
		.setRenderer(TextInput)
		.setRenderProps({ isEditable: { create: true, edit: true }, required: false })
};

export const FORM_LAYOUT_CONFIG = [
	new Row(null, EMPTY_ARRAY, [
		new Column([new Row(BASE_FORM_FIELD_KEYS.NAME)]),
		new Column([new Row(LAUNCH_WIDGET_FORM_FIELD_KEYS.LAUNCH_WIDGET_TYPE)])
	]),
	new Row(null, EMPTY_ARRAY, [
		new Column([new Row(BASE_FORM_FIELD_KEYS.ENABLED)]),
		new Column([new Row(BASE_FORM_FIELD_KEYS.TYPE)])
	]),
	new Row(null, EMPTY_ARRAY, [
		new Column([new Row(BASE_FORM_FIELD_KEYS.IS_ARCHIVED)]),
		new Column([new Row(BASE_FORM_FIELD_KEYS.VERSION)])
	]),
	new Row(BASE_FORM_FIELD_KEYS.CARD_BG, EMPTY_ARRAY, [
		new Column([new Row(BASE_FORM_FIELD_KEYS.CARD_BG_COLOR)]),
		new Column([new Row(BASE_FORM_FIELD_KEYS.CARD_BG_URL)])
	]),
	new Row(LAUNCH_WIDGET_FORM_FIELD_KEYS.VIDEO_META, EMPTY_ARRAY, [
		new Column([new Row(LAUNCH_WIDGET_FORM_FIELD_KEYS.VIDEO_URL)]),
		new Column([new Row(LAUNCH_WIDGET_FORM_FIELD_KEYS.VIDEO_DURATION)])
	]),
	new Row(BASE_FORM_FIELD_KEYS.IS_CROSSABLE),
	new Row(
		BASE_FORM_FIELD_KEYS.CTA,
		EMPTY_ARRAY,
		[new Column([new Row(BASE_FORM_FIELD_KEYS.CTA_TYPE)]), new Column([new Row(BASE_FORM_FIELD_KEYS.CTA_VALUE)])],
		[
			new Row(LAUNCH_WIDGET_FORM_FIELD_KEYS.CTA_PARAMS, EMPTY_ARRAY, [
				new Column([new Row(LAUNCH_WIDGET_FORM_FIELD_KEYS.CTA_PARAMS_GAMENAME)])
			]),
			new Row(LAUNCH_WIDGET_FORM_FIELD_KEYS.CTA_DISPLAY_TEXT, EMPTY_ARRAY, [
				new Column([new Row(LAUNCH_WIDGET_FORM_FIELD_KEYS.CTA_DISPLAY_EN_TEXT)]),
				new Column([new Row(LAUNCH_WIDGET_FORM_FIELD_KEYS.CTA_DISPLAY_HI_TEXT)])
			])
		]
	),

	new Row(null, EMPTY_ARRAY, [
		new Column([new Row(BASE_FORM_FIELD_KEYS.PRIORITY)]),
		new Column([new Row(BASE_FORM_FIELD_KEYS.MIN_GAMES)])
	]),
	new Row(null, EMPTY_ARRAY, [
		new Column([new Row(BASE_FORM_FIELD_KEYS.REPEAT)]),
		new Column([new Row(BASE_FORM_FIELD_KEYS.LIFETIME)])
	]),
	new Row(null, EMPTY_ARRAY, [
		new Column([new Row(BASE_FORM_FIELD_KEYS.VALID_FROM)]),
		new Column([new Row(BASE_FORM_FIELD_KEYS.VALID_TILL)])
	]),
	new Row(null, EMPTY_ARRAY, [new Column([new Row(BASE_FORM_FIELD_KEYS.ABEXPERIMENTS)])]),
	new Row(null, EMPTY_ARRAY, [
		new Column([new Row(BASE_FORM_FIELD_KEYS.INCLUSIVE_SEGMENTS)]),
		new Column([new Row(BASE_FORM_FIELD_KEYS.EXCLUSIVE_SEGMENTS)])
	]),

	new Row(null, EMPTY_ARRAY, [
		new Column([new Row(BASE_FORM_FIELD_KEYS.MAX_APP_VERSION)]),
		new Column([new Row(BASE_FORM_FIELD_KEYS.MIN_APP_VERSION)])
	])
];

export const getLaunchWidgetFormDefaults = () => {
	return {
		type: ENoticeTypes.MANUAL,
		launchWidgetType: ELaunchWidgetType.SINGLE_VIDEO_BASED,
		expiry: 0
	};
};
